import { format, getISOWeek, getISOWeekYear, parseISO, startOfISOWeek, subWeeks } from 'date-fns';
import { nl } from 'date-fns/locale';

import { EMPTY_ANNOTATED_NOTITIE } from '@/apps/agenda/components/detailPanel/annotatedNotitie';
import { Afspraak } from '@/queries/agenda/types';

import { AgendaApiResponse, AgendaLangdurigRow, AgendaWeek, AgendaWeekDay, AgendaWeekRow } from '../types';

const VASTE_TIJD_VAN = '10:00';
const VASTE_TIJD_TOT = '15:00';
const UITLOOP_TIJD = '23:00';

function formatGevraagd(afspraak: Afspraak): string {
  const matTypes = (afspraak.aanvragen ?? [])
    .filter(
      (a): a is Afspraak['aanvragen'][number] & { soort: string; aanduiding?: string; aantal: number } =>
        a.type === 'AanvraagMatTypeDto',
    )
    .map((mat) => {
      const label = mat.aanduiding ? `${mat.soort} ${mat.aanduiding}` : mat.soort;
      return `${mat.aantal} x ${label}`;
    })
    .sort((a, b) => a.localeCompare(b));

  const eenheden = (afspraak.aanvragen ?? [])
    .filter((a): a is Afspraak['aanvragen'][number] & { nummer: string } => a.type === 'AanvraagEenheidDto')
    .map((e) => e.nummer)
    .sort((a, b) => Number(a) - Number(b));

  return [...eenheden, ...matTypes].join(', ');
}

function formatAangemaaktOp(afspraak: Afspraak): string {
  if (!afspraak.aangemaaktOp) return '';
  return format(parseISO(afspraak.aangemaaktOp), 'dd-MM-yyyy HH:mm');
}

function toBaseFields(afspraak: Afspraak) {
  return {
    gevraagd: formatGevraagd(afspraak),
    locatie: afspraak.dienstregelpunt ?? '',
    notitie: afspraak.aanvraagnotitie ?? EMPTY_ANNOTATED_NOTITIE,
    toegekend: (afspraak.toegewezenEenheden ?? []).join(', '),
    notitieUitvoering: afspraak.uitvoeringsnotitie ?? '',
    status: afspraak.status,
    aangemaaktOp: formatAangemaaktOp(afspraak),
    afspraak,
  };
}

function formatTijd(afspraak: Afspraak): string {
  if (!afspraak.van) return '';
  const van = parseISO(afspraak.van);
  const vanStr = format(van, 'HH:mm');
  if (afspraak.tot) {
    const tot = parseISO(afspraak.tot);
    return `${vanStr} - ${format(tot, 'HH:mm')}`;
  }
  if (vanStr === UITLOOP_TIJD) return 'Einde dienst';
  return vanStr;
}

function isHandmatigeTijd(afspraak: Afspraak): boolean {
  if (!afspraak.van || afspraak.relevantVanaf) return false;

  const vanStr = format(parseISO(afspraak.van), 'HH:mm');
  if (!afspraak.tot) return vanStr !== UITLOOP_TIJD;

  const totStr = format(parseISO(afspraak.tot), 'HH:mm');
  return vanStr !== VASTE_TIJD_VAN || totStr !== VASTE_TIJD_TOT;
}

function getDayKey(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function getDayLabel(date: Date): string {
  return format(date, 'EEEEEE d MMMM', { locale: nl }).toLowerCase();
}

function getWeekId(date: Date): string {
  return `${getISOWeekYear(date)}-wk${getISOWeek(date)}`;
}

function getWeekLabel(date: Date): string {
  return `wk${getISOWeek(date)}`;
}

function getWeekTitle(date: Date): string {
  return `Week ${getISOWeek(date)}`;
}

function getCurrentWeekId(): string {
  return getWeekId(new Date());
}

export function transformAfsprakenToAgenda(afspraken: Afspraak[]): AgendaApiResponse {
  const langdurig: AgendaLangdurigRow[] = [];
  const weekMap = new Map<string, { week: AgendaWeek; dayMap: Map<string, AgendaWeekDay>; weekStart: Date }>();

  for (const afspraak of afspraken) {
    if (!afspraak.van || afspraak.relevantVanaf) {
      langdurig.push({
        id: afspraak.id,
        relevantVanaf: afspraak.relevantVanaf ?? undefined,
        ...toBaseFields(afspraak),
      });
      continue;
    }

    const vanDate = parseISO(afspraak.van);
    const weekId = getWeekId(vanDate);

    if (!weekMap.has(weekId)) {
      const weekStart = startOfISOWeek(vanDate);
      weekMap.set(weekId, {
        week: { id: weekId, label: getWeekLabel(vanDate), title: getWeekTitle(vanDate), days: [] },
        dayMap: new Map(),
        weekStart,
      });
    }

    const { dayMap } = weekMap.get(weekId)!;
    const dayKey = getDayKey(vanDate);

    if (!dayMap.has(dayKey)) {
      dayMap.set(dayKey, { id: dayKey, label: getDayLabel(vanDate), rows: [] });
    }

    const row: AgendaWeekRow = {
      id: afspraak.id,
      tijd: formatTijd(afspraak),
      tijdIsHandmatig: isHandmatigeTijd(afspraak),
      ...toBaseFields(afspraak),
    };

    dayMap.get(dayKey)!.rows.push(row);
  }

  // Ensure current week and previous week are always present when there is any week data
  if (weekMap.size > 0) {
    const today = new Date();
    for (const d of [today, subWeeks(today, 1)]) {
      const wid = getWeekId(d);
      if (!weekMap.has(wid)) {
        weekMap.set(wid, {
          week: { id: wid, label: getWeekLabel(d), title: getWeekTitle(d), days: [] },
          dayMap: new Map(),
          weekStart: startOfISOWeek(d),
        });
      }
    }
  }

  // Sort weeks by week start date, then fill days into each week sorted by date
  const sortedWeekEntries = [...weekMap.entries()].sort((a, b) => a[1].weekStart.getTime() - b[1].weekStart.getTime());

  const weeks: AgendaWeek[] = sortedWeekEntries.map(([, { week, dayMap }]) => {
    week.days = [...dayMap.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, day]) => {
        day.rows.sort((a, b) => (a.afspraak.van ?? '').localeCompare(b.afspraak.van ?? ''));
        return day;
      });
    return week;
  });

  const currentWeekId = getCurrentWeekId();
  const selectedWeekId = weeks.some((w) => w.id === currentWeekId) ? currentWeekId : (weeks[0]?.id ?? '');

  return {
    langdurig,
    week: { selectedWeekId, weeks },
  };
}
